import React from 'react';
import { Stack } from '@mui/material';

import DialogJsonField from './DialogJsonField';
import ScraperActionInput from './gatewaySettings/ScraperActionInput';
import { AppDataContext } from '../../context/Context';
import { scraperActionKeys } from '../../utils/scraperSettings';
import i18n from '../../i18n';

const SCENARIOS_MODE = 'scenarios';

// The gateway API whose driver reads scenario references (gateway_apis.name, the
// key core resolves the driver by).
const XSCRAPER_API = 'xscraper';

/**
 * A gateway settings row's settings: DialogJsonField's Fields and Raw modes, plus
 * **Scenarios** for xscraper rows.
 *
 * Each xscraper action (payin_init, payout_make, …) runs either inline `code`
 * or a saved scraper scenario, referenced as `scenario` (by name) or
 * `scenario_id`. Scenarios mode shows one row per action and edits only that:
 * every change rewrites the action's code / scenario / scenario_id keys, leaving
 * exactly one, and keeps its `input` — which holds the bank credentials — its
 * `proxy` and anything else as they were. Each row is a ScraperActionInput, the
 * widget Form mode (DialogGatewaySettingsField) shows the same actions with.
 *
 * Offered only when the JSON has at least one such action, and not on a row
 * whose API is known to be something other than xscraper: another integration's
 * settings may well have a nested object with a `code` in it, and offering to
 * replace a bank code with a script would be nonsense.
 *
 * The value contract is the base class's, untouched: this mode only ever emits a
 * parsed object, and is not offered while the raw text does not parse.
 */
export default class DialogScraperSettingsJsonField extends DialogJsonField {
    static contextType = AppDataContext;

    static getDerivedStateFromProps(nextProps, prevState) {
        const next = DialogJsonField.getDerivedStateFromProps(nextProps, prevState);
        // The base falls back to Raw for any value it cannot show as Fields, which
        // is most settings. A reset or another record that still has
        // actions keeps Scenarios open.
        if (
            next &&
            prevState.mode === SCENARIOS_MODE &&
            scraperActionKeys(nextProps.value).length > 0
        ) {
            return { ...next, mode: SCENARIOS_MODE };
        }
        return next;
    }

    componentDidUpdate() {
        // The mode can stop being on offer while it is open — the gateway API was
        // changed to another integration, say. Raw always is.
        if (this.state.mode === SCENARIOS_MODE && !this.scenariosOffered()) {
            this.setState({ mode: 'raw' });
        }
    }

    store() {
        return this.props.context || this.context;
    }

    /** The scenario list GatewaySettingsPage loads (optionsFrom), not archived. */
    scenarios() {
        return this.store()?.getState().controllersData?.publicScraperScenarios || [];
    }

    scenariosOffered() {
        if (scraperActionKeys(this.props.value).length === 0) return false;
        const apiId = this.props.data?.gatewayApiId;
        const apis = this.store()?.getState().controllersData?.publicGatewayApis || [];
        const api = apis.find((item) => String(item.id) === String(apiId));
        // Unknown — no API chosen yet, or a list this role cannot read — leaves
        // the decision to the JSON.
        return !api || api.name === XSCRAPER_API;
    }

    extraModes() {
        if (!this.scenariosOffered()) return [];
        return [{ value: SCENARIOS_MODE, label: i18n.t('scraperScenarios.modeScenarios') }];
    }

    helperLine(mode, fieldsPossible) {
        if (mode !== SCENARIOS_MODE) return super.helperLine(mode, fieldsPossible);
        return this.props.error || i18n.t('scraperScenarios.modeScenariosHelper');
    }

    renderModeBody(mode) {
        if (mode !== SCENARIOS_MODE) return null;

        const disabled = Boolean(this.props.field.onlyRead);
        return (
            <Stack spacing={1}>
                {scraperActionKeys(this.props.value).map((key) => (
                    <ScraperActionInput
                        key={key}
                        actionKey={key}
                        action={this.props.value[key]}
                        scenarios={this.scenarios()}
                        store={this.store()}
                        disabled={disabled}
                        // Read when called: an inlined scenario's code arrives after a fetch.
                        onChange={(action) => this.emitValue({ ...this.props.value, [key]: action })}
                    />
                ))}
            </Stack>
        );
    }
}
